import { Dispatch, SetStateAction, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useStore } from 'zustand';
import { shallow } from 'zustand/shallow';
import { createStore, StoreApi } from 'zustand/vanilla';

import { UseRiderOptionsHook } from '../../hooks/useRiderOptions';
import { RideSuggestion, useSmartPoolingSuggestion } from '../../hooks/useSmartPoolingSuggestion';

import { useRideEditorState } from './useRideEditorState';
import { useRideLiveUpdates } from './useRideLiveUpdates';
import { RIDE_QUERY_KEY, useRideQuery } from './useRideQuery';

interface RouteLeg {
  arrivesAt: string;
  arrivesAtMax: string;
  departsAt: string;
  distance: number;
  duration: number;
  endLoc: number;
  endsAt: string;
  startLoc: number;
}

export interface RideEditorState {
  rideDetails?: RideDetails;

  setRideDetails: Dispatch<SetStateAction<RideDetails | undefined>>;
  validatedWaypoints: WaypointFormType[];
  setValidatedWaypoints: Dispatch<SetStateAction<WaypointFormType[]>>;
  waypointsOnMap: WaypointMarker[];
  raWaypointOnMap: WaypointMarker | null;
  updateAnchorTimeAndMap: (rideDetails: RideDetails) => void;
  updateWaypointsOnMap: (updatedWaypoints) => void;
  updateRAWaypointOnMap: (waypoints: WaypointFormType[], mapWaypoints?: WaypointMarker[]) => void;
  swapWaypointPositionOnMap: (currentIndex, newIndex) => void;
  fetchRideDetails: (rideId) => void;
  getRiderOptions: () => UseRiderOptionsHook;
  completeAnchorTime?: Date;
  updateAnchorTime: (updatedAnchorTime) => void;
  handleEstimatedTime: (routeLegs: RouteLeg[] | SuggestedLeg[]) => void;
  routeEstimates: WaypointMarker[];
  isFetching: boolean;
  isLoadingError?: boolean;
  updateTripTemplateForSmartPooling: (updatedTripTemplateAttributes?: RideSeriesAttributes) => void;
  tripTemplateForSmartPooling?: RideSeriesAttributes;
  hasSmartPoolingSuggestion: boolean;
  suggestedRouteDetails?: SuggestedRouteDetails;
  suggestionData?: RideSuggestion;
}

type SharedEditorState = Omit<RideEditorState, 'setRideDetails' | 'fetchRideDetails' | 'isFetching' | 'isLoadingError'>;
type EditorStore = StoreApi<SharedEditorState | null>;
const emptyStore = createStore<SharedEditorState | null>(() => null);
const sessions = new WeakMap<QueryClient, Map<string, EditorStore>>();

function getSessions(queryClient: QueryClient) {
  let stores = sessions.get(queryClient);

  if (!stores) {
    stores = new Map();
    sessions.set(queryClient, stores);
  }

  return stores;
}

export function useRideEditorScope() {
  const { pathname } = useLocation();

  return pathname.replace(/\/riders\/new\/?$/, '');
}

function useSessionIdentity() {
  const queryClient = useQueryClient();
  const key = useRideEditorScope();

  return { queryClient, key };
}

// Forward actions to the latest committed owner render while keeping consumer
// effect dependencies stable. Only data changes publish a new store snapshot.
type ActionKeys<T> = {
  [K in keyof T]: T[K] extends (...args: never[]) => unknown ? K : never;
}[keyof T];

function stableActions<T extends object>(initial: T, current: () => T): Pick<T, ActionKeys<T>> {
  const actions: Record<string, unknown> = {};

  Object.entries(initial).forEach(([key, value]) => {
    if (typeof value === 'function') {
      actions[key] = (...args: unknown[]) => {
        const action = (current() as Record<string, unknown>)[key] as (...values: unknown[]) => unknown;

        return action(...args);
      };
    }
  });

  return actions as Pick<T, ActionKeys<T>>;
}

/** Mount once in a page keyed by useRideEditorScope(). */
export function useRideEditorSession() {
  const { queryClient, key } = useSessionIdentity();
  const [store] = useState(() => createStore<SharedEditorState | null>(() => null));
  const [ready, setReady] = useState(false);
  const serverState = useRideQuery();

  useRideLiveUpdates(serverState);
  const { shouldUseSmartpooling, suggestionData, suggestedRideDetails, suggestedRouteDetails } =
    useSmartPoolingSuggestion(serverState.rideDetails);
  const rideDetails = shouldUseSmartpooling ? suggestedRideDetails : serverState.rideDetails;
  const clientState = useRideEditorState(rideDetails);
  const value: SharedEditorState = {
    rideDetails,
    ...clientState,
    hasSmartPoolingSuggestion: suggestedRideDetails !== undefined,
    suggestedRouteDetails,
    suggestionData,
  };
  const committed = useRef(value);
  const [actions] = useState(() => stableActions(value, () => committed.current));
  const [riderActions] = useState(() =>
    stableActions(value.getRiderOptions(), () => committed.current.getRiderOptions())
  );
  const previousRiders = useRef<UseRiderOptionsHook>();

  useLayoutEffect(() => {
    const stores = getSessions(queryClient);

    stores.set(key, store);
    setReady(true);

    return () => {
      if (stores.get(key) === store) {
        stores.delete(key);
        if (['/rides/new', '/rideseries/new', '/rides/duplicate'].includes(key)) {
          // StrictMode replays effect cleanup/setup without leaving the page.
          // Wait for that setup before deciding this draft session has ended.
          void Promise.resolve().then(() => {
            if (!stores.has(key)) {
              queryClient.removeQueries({ queryKey: [RIDE_QUERY_KEY, key], exact: true });
            }
          });
        }
      }
    };
  }, [queryClient, key, store]);

  useLayoutEffect(() => {
    committed.current = value;
    const riders = { ...value.getRiderOptions(), ...riderActions };
    const previous = store.getState();
    const sameRiders = previousRiders.current && shallow(previousRiders.current, riders);
    const getRiderOptions = sameRiders && previous ? previous.getRiderOptions : () => riders;
    const next = { ...value, ...actions, getRiderOptions };

    previousRiders.current = riders;
    if (!previous || !shallow(previous, next)) {
      store.setState(next, true);
    }
  });

  return { ...serverState, ready };
}

export function useRideEditor(): RideEditorState {
  const { queryClient, key } = useSessionIdentity();
  const store = getSessions(queryClient).get(key);

  const state = useStore(store || emptyStore);
  const { setRideDetails, fetchRideDetails, isFetching, isLoadingError } = useRideQuery({ refetchOnMount: false });

  if (!state) {
    throw new Error('useRideEditor requires an initialized ride page session');
  }

  return { ...state, setRideDetails, fetchRideDetails, isFetching, isLoadingError };
}
